import { ChangeTimestampEventProps } from './useTimestampTracking'
import { GetConnectionTypes } from '@dtx-company/inter-app/src/components/AnalyticsWelcomeToast/types'
import { Intervals } from '@dtx-company/inter-app/src/constants/dateFilter'
import { differenceInHours, formatRFC3339 } from 'date-fns'
import { useCallback } from 'react'
import { useSendAnalyticsWelcomeToastNotification } from '@dtx-company/inter-app/src/components/AnalyticsWelcomeToast/useSendAnalyticsWelcomeToast'
import { useWelcomeToastData } from '@dtx-company/inter-app/src/components/AnalyticsWelcomeToast/AnalyticsWelcomeToast.hooks'

export interface UseSendWelcomeToastEventHelpers {
  sendWelcomeToastEvent(eventProps: ChangeTimestampEventProps): Promise<void>
}

export const useSendWelcomeToastEvent = (): UseSendWelcomeToastEventHelpers => {
  const sendAnalyticsWelcomeToastNotification = useSendAnalyticsWelcomeToastNotification()
  const { getWelcomeToastData } = useWelcomeToastData()

  const sendWelcomeToastEvent = useCallback(
    async ({ timestamp, prevTimestamp }: ChangeTimestampEventProps): Promise<void> => {
      const timeDifference = differenceInHours(timestamp, prevTimestamp)

      const minimumSendingRequirements = {
        withData: 1,
        withoutData: 24
      }

      if (timeDifference >= minimumSendingRequirements.withData) {
        const dateFilters = {
          start_date: formatRFC3339(prevTimestamp),
          end_date: formatRFC3339(timestamp),
          interval: Intervals.HOURLY
        }

        const welcomeToastData = await getWelcomeToastData({
          dateFilters,
          dataTypes: [GetConnectionTypes.SCANS, GetConnectionTypes.LINK_CLICKS]
        })
        const { scans, linkClicks, pageViews } = welcomeToastData

        const hasData = scans || linkClicks || pageViews
        if (hasData || (!hasData && timeDifference >= minimumSendingRequirements.withoutData)) {
          sendAnalyticsWelcomeToastNotification({
            dateFilters,
            welcomeToastData
          })
        }
      }
    },
    [getWelcomeToastData, sendAnalyticsWelcomeToastNotification]
  )

  return {
    sendWelcomeToastEvent
  }
}
